/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { bySequence } from '@linc-technologies/linced-common/utils/sorting';

export default class TopicsController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  Scopes = Scopes;

  @tracked
  subjectDialogOpen = false;

  subjectSaveInProgress = false;

  @tracked
  editingSubject = null;

  @tracked
  subjectLabel = '';

  @action
  setSubjectLabel(value) {
    this.subjectLabel = value;
  }

  @tracked
  subjectDescription = '';

  @action
  setSubjectDescription(value) {
    this.subjectDescription = value;
  }

  @tracked
  subjectTrackable = false;

  @tracked
  subjectBestFits = false;

  @tracked
  subjectVisibleToTeachers = true;

  @action
  setSubjectVisibleToTeachers(value) {
    this.subjectVisibleToTeachers = value;
  }

  @tracked
  subjectVisibleToParents = false;

  @tracked
  subjectVisibleToLearners = false;

  @tracked
  subjectIsGradable = false;

  @tracked
  subjectIsTopic = false;

  @tracked
  showRadialDials = false;

  @tracked
  sortSubjectsDialogOpen;

  @tracked
  tab;

  @action
  setTab(value) {
    this.tab = value;
  }

  @computed('session.scopes.[]')
  get isTenantAdmin() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  // subjects provides a sorted, filtered list of subjects which have the
  // "isTopic" flag set, and which have been saved (so have an ID).
  @computed('model.@each.{isTopic,id}')
  get subjects() {
    return this.model
      .filter((subject) => {
        return subject.isTopic && !isEmpty(subject.id);
      })
      .sort(bySequence);
  }

  reset() {
    this.tab = null;
  }

  @action
  addSubject() {
    this.resetSubject();
    this.subjectDialogOpen = true;
  }

  @action
  sortSubjects() {
    this.sortSubjectsDialogOpen = true;
  }

  @action
  editSubject(subject) {
    this.resetSubject();
    this.editingSubject = subject;
    this.subjectLabel = subject.label;
    this.subjectDescription = subject.description;
    this.subjectTrackable = subject.trackable;
    this.subjectBestFits = subject.calculateBestFits;
    this.subjectVisibleToTeachers = subject.visibleToTeachers;
    this.subjectVisibleToParents = subject.visibleToParents;
    this.subjectVisibleToLearners = subject.visibleToLearners;
    this.subjectIsGradable = subject.isGradable;
    this.subjectIsTopic = subject.isTopic;
    this.subjectDialogOpen = true;
    this.showRadialDials = subject.showRadialDials;
  }

  @action
  closeSubjectDialog() {
    this.subjectDialogOpen = false;
  }

  @action
  closeSortSubjectsDialog() {
    this.sortSubjectsDialogOpen = false;
  }

  @action
  saveSubject() {
    let subject = this.editingSubject;
    let toaster = this.paperToaster;

    if (isEmpty(subject)) {
      subject = this.store.createRecord('goals/subject', {
        schoolId: this.get('session.school'),
      });
    }

    subject.setProperties({
      label: this.subjectLabel,
      description: this.subjectDescription,
      trackable: this.subjectTrackable,
      calculateBestFits: this.subjectBestFits,
      visibleToTeachers: this.subjectVisibleToTeachers,
      visibleToParents: this.subjectVisibleToParents,
      visibleToLearners: this.subjectVisibleToLearners,
      showRadialDials: this.showRadialDials,
      isGradable: this.subjectIsGradable,
      isTopic: this.subjectIsTopic,
    });

    subject
      .save()
      .then(() => {
        this.subjectDialogOpen = false;
        toaster.show(`Goals subject "${subject.get('label')}" saved.`, {
          duration: 4000,
          position: 'top right',
        });
      })
      .catch(() => {
        toaster.show(`Error saving goals subject.`, {
          duration: 4000,
          position: 'top right',
        });
      });
  }

  resetSubject() {
    this.editingSubject = null;
    this.subjectLabel = '';
    this.subjectDescription = '';
    this.subjectTrackable = false;
    this.subjectBestFits = false;
    this.subjectVisibleToTeachers = true;
    this.subjectVisibleToParents = false;
    this.subjectVisibleToLearners = false;
    this.subjectIsGradable = false;
    this.subjectIsTopic = true;
    this.showRadialDials = false;
  }
}
